import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { createSubscription } from "../services/createSubscription";
import { fetchDetails } from "../services/fetchDetails";
import { fetchProducts } from "../services/fetchProducts";
import { retryPayment } from "../services/retryPayment";
import { unCancelSubscription } from "../services/unCancelSubscription";
import { unSkipSubscription } from "../services/unSkipSubscription";
import type { ProductData } from "../types/productData";
import type { SubscriptionDetails } from "../types/subscription";
import type { SubscriptionSchema } from "../types/subscriptionSchema";

const initialState: SubscriptionSchema = {};

export const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    resetSuccess: (state) => {
      state.isSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSubscription.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.error = undefined;
      })
      .addCase(createSubscription.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(createSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(retryPayment.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.error = undefined;
      })
      .addCase(retryPayment.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(retryPayment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(unCancelSubscription.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.error = undefined;
      })
      .addCase(unCancelSubscription.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(unCancelSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(unSkipSubscription.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.error = undefined;
      })
      .addCase(unSkipSubscription.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(unSkipSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchDetails.pending, (state) => {
        state.isFetchingDetails = true;
        state.error = undefined;
      })
      .addCase(fetchDetails.fulfilled, (state, action: PayloadAction<SubscriptionDetails>) => {
        state.isFetchingDetails = false;
        state.details = action.payload;
      })
      .addCase(fetchDetails.rejected, (state, action) => {
        if (action.payload.code === "ERR_CANCELED") {
          return;
        }
        state.isFetchingDetails = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchProducts.pending, (state) => {
        state.productsAreLoading = true;
      })
      .addCase(fetchProducts.fulfilled, (state, action: PayloadAction<ProductData[]>) => {
        state.productsAreLoading = false;
        state.productsList = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.productsAreLoading = false;
        if (action.payload.code === "ERR_CANCELED") {
          return;
        }
      });
  },
});

export const { reducer: subscriptionReducer, actions: subscriptionActions } = subscriptionSlice;
